import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { DebtCase, DebtCaseStage, ApiResponse } from '@/types'

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const debtCaseKeys = {
  all: ['debt_cases'] as const,
  lists: () => [...debtCaseKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...debtCaseKeys.lists(), filters] as const,
  details: () => [...debtCaseKeys.all, 'detail'] as const,
  detail: (id: number) => [...debtCaseKeys.details(), id] as const,
}

// ─── Filters ─────────────────────────────────────────────────────────────────

export interface DebtCaseFilters {
  stage?: DebtCaseStage | ''
  customer_id?: number
  property_id?: number
  customer_search?: string
  // EP-97: user-selectable server-side sort. Default view (no stage) is the
  // COLLECTIONS queue — unresolved cases, most-overdue-first (due_at asc).
  sort?: string
  sort_order?: string
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useDebtCases(page = 1, filters: DebtCaseFilters = {}) {
  return useQuery({
    queryKey: debtCaseKeys.list({ page, ...filters }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (filters.stage) params.stage = filters.stage
      if (filters.customer_id) params.customer_id = filters.customer_id
      if (filters.property_id) params.property_id = filters.property_id
      if (filters.customer_search) params.customer_search = filters.customer_search
      if (filters.sort) params.sort = filters.sort
      if (filters.sort_order) params.sort_order = filters.sort_order
      const { data } = await api.get<ApiResponse<DebtCase[]>>('/api/debt_cases', { params })
      return data
    },
  })
}

export function useDebtCase(id: number) {
  return useQuery({
    queryKey: debtCaseKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<DebtCase>>(`/api/debt_cases/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

export function useEscalateDebtCase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, target_stage }: { id: number; target_stage?: DebtCaseStage }) => {
      const { data } = await api.patch<ApiResponse<DebtCase>>(`/api/debt_cases/${id}/escalate`, {
        target_stage,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, debtCaseKeys.lists())
      invalidateDetailQueries(queryClient, debtCaseKeys.detail(data.id))
    },
  })
}

export function useResolveDebtCase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.patch<ApiResponse<DebtCase>>(`/api/debt_cases/${id}/resolve`)
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, debtCaseKeys.lists())
      invalidateDetailQueries(queryClient, debtCaseKeys.detail(data.id))
    },
  })
}

export function useAddDebtCaseNote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, note }: { id: number; note: string }) => {
      const { data } = await api.patch<ApiResponse<DebtCase>>(`/api/debt_cases/${id}/add_note`, {
        note,
      })
      return data.data
    },
    onSuccess: (data) => {
      invalidateDetailQueries(queryClient, debtCaseKeys.detail(data.id))
    },
  })
}

export function useMarkNoticeSent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.patch<ApiResponse<DebtCase>>(
        `/api/debt_cases/${id}/mark_notice_sent`,
      )
      return data.data
    },
    onSuccess: (data) => {
      invalidateDetailQueries(queryClient, debtCaseKeys.detail(data.id))
    },
  })
}
